/**
 * Call Follow-Up Hooks (Application)
 *
 * React Query hooks for call follow-up data fetching.
 * Uses the repository pattern from clean architecture.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { callFollowUpRepository } from '../../infrastructure/repositories';
import type { CallFollowUpSearchRequest } from '../../domain/types/requests';

// ============================================================================
// Query Keys Factory Pattern
// ============================================================================

export const callFollowUpKeys = {
  all: ['callFollowUps'] as const,
  lists: () => [...callFollowUpKeys.all, 'list'] as const,
  search: (request?: CallFollowUpSearchRequest) => [...callFollowUpKeys.all, 'search', request] as const,
  options: () => [...callFollowUpKeys.all, 'options'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to search call follow-ups with filters, sorting, and pagination
 */
export function useCallFollowUpSearch(request: CallFollowUpSearchRequest) {
  return useQuery({
    queryKey: callFollowUpKeys.search(request),
    queryFn: () => callFollowUpRepository.searchCallFollowUps(request),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch search options (columns and presets)
 */
export function useCallFollowUpSearchOptions() {
  return useQuery({
    queryKey: callFollowUpKeys.options(),
    queryFn: () => callFollowUpRepository.getSearchOptions(),
    staleTime: 10 * 60 * 1000, // 10 minutes - options rarely change
  });
}
